/**
 * useIdleLogout — fires a logout callback after N seconds of no user input,
 * and exposes the live remaining-time so consumers can render a countdown.
 *
 * Used by the kiosk page to auto-log-out a teacher who walked away.
 * Resets on any keydown / mousemove / touchstart / paste / click. Pause
 * entirely with `enabled: false` (also nulls remainingMs).
 *
 * The 1 Hz tick that drives `remainingMs` only runs while enabled — no
 * background work when nobody is signed in.
 */
import { useEffect, useRef, useState } from "react";

interface UseIdleLogoutArgs {
  enabled: boolean;
  timeoutMs: number;
  onTimeout: () => void;
}

interface UseIdleLogoutResult {
  /** Milliseconds remaining until onTimeout fires. null when disabled. */
  remainingMs: number | null;
}

export function useIdleLogout({
  enabled,
  timeoutMs,
  onTimeout,
}: UseIdleLogoutArgs): UseIdleLogoutResult {
  const cbRef = useRef(onTimeout);
  useEffect(() => {
    cbRef.current = onTimeout;
  }, [onTimeout]);

  const [remainingMs, setRemainingMs] = useState<number | null>(null);

  useEffect(() => {
    if (!enabled || typeof window === "undefined") {
      setRemainingMs(null);
      return;
    }

    let deadline = Date.now() + timeoutMs;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let tickId: ReturnType<typeof setInterval> | null = null;

    const fire = () => cbRef.current?.();

    const reset = () => {
      deadline = Date.now() + timeoutMs;
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(fire, timeoutMs);
      setRemainingMs(timeoutMs);
    };

    const events: (keyof WindowEventMap)[] = [
      "keydown",
      "mousemove",
      "touchstart",
      "paste",
      "click",
    ];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();

    // Tick once a second so the UI countdown updates. We don't try to do
    // sub-second precision — the value is for human display.
    tickId = setInterval(() => {
      setRemainingMs(Math.max(0, deadline - Date.now()));
    }, 1000);

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (tickId) clearInterval(tickId);
      events.forEach((e) => window.removeEventListener(e, reset));
      setRemainingMs(null);
    };
  }, [enabled, timeoutMs]);

  return { remainingMs };
}
